import { h, isVNode } from 'vue';
import type { DataTableColumnDef } from '@alphadoc/alphadoc-table-grid';
import { DateTime, type DateTimeFormatOptions } from 'luxon';
import i18n from '@/i18n.ts';
import type { SQLDate } from '@/modules/common/types/sql-date.type.ts';
import UiEllipsisText from '@/modules/ui/components/ui/UiEllipsisText.vue';
import { ElementType } from '@/modules/viewer/enums/element-type.enum.ts';
import type { EnumOptions } from '@/modules/viewer/types/elements.type.ts';
import { LuxonUtil } from '@/modules/common/utils/luxon.util.ts';

type SectionTableColumnOptions<TData> = {
  type?: ElementType;
  cell?: (value: unknown, row: TData) => unknown;
  enumOptions?: EnumOptions;
  distinct?: boolean;
};

function renderSectionTableCell(content: unknown) {
  if (content == null || content === '') {
    return '';
  }

  if (isVNode(content)) {
    return content;
  }

  if (typeof content === 'string' || typeof content === 'number' || typeof content === 'boolean') {
    return h(UiEllipsisText, { text: String(content), placement: 'bottom' });
  }

  return content;
}

export function sectionTableColumn<TData>(
  id: string,
  accessor: (row: TData) => unknown,
  size: number,
  options?: SectionTableColumnOptions<TData>
): DataTableColumnDef<TData> {
  return {
    id,
    header: `core.sectionTable.columns.${id}`,
    accessor,
    size,
    enableSorting: true,
    enableColumnFilter: true,
    cell: (value, row) => renderSectionTableCell(options?.cell ? options.cell(value, row) : value),
    meta: {
      type: options?.type ?? ElementType.String,
      ...(options?.enumOptions !== undefined ? { options: options.enumOptions } : {}),
      ...(options?.distinct ? { distinct: true } : {}),
    } as DataTableColumnDef<TData>['meta'],
  };
}

export function sectionTableDateColumn<TData>(
  id: string,
  accessor: (row: TData) => SQLDate | null | undefined,
  size: number,
  format: DateTimeFormatOptions = DateTime.DATETIME_SHORT
): DataTableColumnDef<TData> {
  return sectionTableColumn(id, accessor, size, {
    type: ElementType.Date,
    cell: (value) => {
      const date = value as SQLDate | null | undefined;

      if (!date) {
        return '';
      }

      return LuxonUtil.formatSQL(date, String(i18n.global.locale.value), format) ?? '';
    },
  });
}

export function sectionTableNumberColumn<TData>(
  id: string,
  accessor: (row: TData) => unknown,
  size: number
): DataTableColumnDef<TData> {
  return sectionTableColumn(id, accessor, size, {
    type: ElementType.Number,
  });
}
